import { Component, DoCheck, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ClientAdminJanusComponent } from '../../master-views/admin.view/client-admin-janus/client-admin-janus.component';

@Component({
  selector: 'wm-users-janus',
  standalone:false,
  templateUrl: './users-janus.component.html',
  styleUrl: './users-janus.component.scss'
})
export class UsersJanusComponent implements OnInit, OnDestroy, DoCheck {
  activeTab: 'list' | 'roles' | 'invite' = 'list';
  private routerSub?: Subscription;
  private redirectingToAllowedTab = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    public clientAdmin: ClientAdminJanusComponent
  ) {}

  ngOnInit(): void {
    this.updateActiveTabFromUrl(this.router.url);

    this.routerSub = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.updateActiveTabFromUrl(event.urlAfterRedirects);
      });
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
  }

  ngDoCheck(): void {
    // Added by Amisha on 2026-09-18.
    // Reason: permission flags resolve in the parent shell asynchronously, so redirect once the first allowed Users tab is known.
    this.redirectToFirstVisibleTab();
  }

  switchTab(tab: 'list' | 'roles' | 'invite') {
    this.activeTab = tab;
  }

  isTabActive(tab: 'list' | 'roles' | 'invite'): boolean {
    if (this.canViewTab(this.activeTab)) {
      return this.activeTab === tab;
    }

    return this.getFirstVisibleTab() === tab;
  }

  private canViewTab(tab: 'list' | 'roles' | 'invite'): boolean {
    if (tab === 'list') return this.clientAdmin.canEditUsers;
    if (tab === 'roles') return this.clientAdmin.canEditRoles;
    return this.clientAdmin.canInviteUsers;
  }

  private getFirstVisibleTab(): 'list' | 'roles' | 'invite' | null {
    if (this.clientAdmin.canEditUsers) return 'list';
    if (this.clientAdmin.canEditRoles) return 'roles';
    if (this.clientAdmin.canInviteUsers) return 'invite';
    return null;
  }

  private redirectToFirstVisibleTab(): void {
    if (this.redirectingToAllowedTab || this.canViewTab(this.activeTab)) {
      return;
    }

    const firstVisibleTab = this.getFirstVisibleTab();

    if (!firstVisibleTab) {
      return;
    }

    this.redirectingToAllowedTab = true;
    this.activeTab = firstVisibleTab;

    const route = firstVisibleTab === 'invite'
      ? 'invitations'
      : firstVisibleTab;

    this.router.navigate([route], {
      relativeTo: this.route,
      replaceUrl: true
    }).finally(() => {
      this.redirectingToAllowedTab = false;
    });
  }

  private updateActiveTabFromUrl(url: string): void {
    if (url.includes('/users/roles')) {
      this.activeTab = 'roles';
      return;
    }

    if (url.includes('/users/invitations')) {
      this.activeTab = 'invite';
      return;
    }

    this.activeTab = 'list';
  }
}
